import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart, Coffee } from 'lucide-react';
import { motion } from 'framer-motion';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const KampalaFoodGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Kampala's Best Street Food Guide | TravelCord Blog";
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "Kampala's Best Street Food Guide";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  const foodItems = [
    {
      name: "Rolex",
      description: "Uganda's most famous street food - a chapati rolled with eggs, vegetables, and sometimes meat.",
      image: "https://imgur.com/4kwMnNu.jpg",
      where: "Available throughout Kampala, especially around universities and busy streets."
    },
    {
      name: "Kikomando",
      description: "Chapati cut into pieces and mixed with beans - a filling and affordable meal.",
      image: "https://imgur.com/4kwMnNu.jpg",
      where: "Local food stalls and small restaurants across the city."
    },
    {
      name: "Muchomo",
      description: "Skewered and grilled meat (usually goat, beef, or chicken) seasoned with local spices.",
      image: "https://imgur.com/4kwMnNu.jpg",
      where: "Evening street vendors, especially in areas like Kabalagala and Bugolobi."
    },
    {
      name: "Grasshoppers (Nsenene)",
      description: "A seasonal delicacy - fried grasshoppers seasoned with onions and spices.",
      image: "https://imgur.com/4kwMnNu.jpg",
      where: "Seasonal availability, found at markets and street vendors during rainy seasons."
    },
    {
      name: "Katogo",
      description: "A breakfast dish made of matoke (plantains) cooked with offal, beans, or groundnut sauce.",
      image: "https://imgur.com/4kwMnNu.jpg",
      where: "Local restaurants and food stalls in the morning."
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/4kwMnNu.jpg"
            alt="Kampala Street Food"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/lCTURxX.jpg"
                alt="Sarah Namukasa"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Sarah Namukasa</h3>
                <p className="text-white/80 text-sm">Food & Culture Guide</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-orange-500/20 rounded-full px-2.5 py-0.5 text-xs text-orange-500 mb-4 backdrop-blur-sm">
                Food & Culture
              </div>
              
              <h1 className="text-4xl font-bold">Kampala's Best Street Food Guide</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  March 25, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  8 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Kampala
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Kampala's vibrant street food scene offers a delicious window into Uganda's culinary culture. From quick bites on the go to hearty meals that fuel the city, street food in Kampala is affordable, authentic, and bursting with flavor. This guide will take you through the must-try street foods, where to find them, and how to enjoy them like a local.
                </p>

                <h2>Must-Try Street Foods in Kampala</h2>

                {foodItems.map((food, index) => (
                  <div key={index} className="my-8">
                    <h3>{food.name}</h3>
                    <p>{food.description}</p>
                    <p><strong>Where to find it:</strong> {food.where}</p>
                  </div>
                ))}

                <div className="bg-gray-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Street Food Safety Tips</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Look for busy stalls with high turnover - this ensures freshness</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Watch food being prepared to ensure proper handling</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Stick to cooked foods that are served hot</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Carry hand sanitizer or wet wipes for before and after eating</span>
                    </li>
                  </ul>
                </div>

                <h2>Best Street Food Areas in Kampala</h2>
                <p>
                  While street food can be found throughout Kampala, certain areas are known for their concentration of quality vendors:
                </p>
                <ul>
                  <li><strong>Wandegeya</strong> - Near Makerere University, this area is famous for rolex stands and affordable student favorites.</li>
                  <li><strong>Kabalagala</strong> - Known as Kampala's "food street," with vendors selling everything from local to international street foods.</li>
                  <li><strong>Nakasero Market</strong> - While primarily a produce market, the periphery has excellent food stalls.</li>
                  <li><strong>Bugolobi Market</strong> - Great for evening muchomo (grilled meat) and other local delicacies.</li>
                  <li><strong>Owino Market</strong> - The largest market in Uganda also has numerous food vendors around its edges.</li>
                </ul>

                <h2>Beyond Street Food: Local Restaurants</h2>
                <p>
                  If you want to experience Ugandan cuisine in a sit-down setting, consider these local restaurants:
                </p>
                <ul>
                  <li><strong>The Lawns</strong> - Offers traditional Ugandan dishes in a garden setting.</li>
                  <li><strong>2K Restaurant</strong> - Famous for local food served buffet-style.</li>
                  <li><strong>Fasika Ethiopian Restaurant</strong> - While Ethiopian, it's popular with locals and offers a different East African cuisine experience.</li>
                  <li><strong>Cafesserie</strong> - For those wanting a break from street food, this offers international cuisine with Ugandan influences.</li>
                </ul>

                <h2>Food Tours in Kampala</h2>
                <p>
                  For those who prefer guided experiences, several companies offer food tours in Kampala:
                </p>
                <ul>
                  <li><strong>TravelCord Food Tours</strong> - Our specialized food tours take you through markets and street food areas with expert local guides.</li>
                  <li><strong>Kampala Walking Tours</strong> - Offers food-focused walking tours of the city center.</li>
                  <li><strong>Taste of Uganda</strong> - Provides immersive cooking and tasting experiences.</li>
                </ul>

                <div className="my-8">
                  <img 
                    src="https://imgur.com/4kwMnNu.jpg" 
                    alt="Kampala Food Market"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    A typical food market in Kampala with various local delicacies
                  </p>
                </div>

                <h2>Etiquette and Cultural Tips</h2>
                <p>
                  Understanding local food customs enhances your experience:
                </p>
                <ul>
                  <li><strong>Handwashing</strong> - Many local eateries provide water and soap for handwashing before meals.</li>
                  <li><strong>Eating with Hands</strong> - It's common and acceptable to eat certain foods with your right hand.</li>
                  <li><strong>Bargaining</strong> - While prices are generally fixed at established stalls, light bargaining might be expected at markets.</li>
                  <li><strong>Tipping</strong> - Not expected for street food, but appreciated at restaurants (5-10%).</li>
                </ul>

                <h2>Conclusion</h2>
                <p>
                  Kampala's street food scene offers a delicious, affordable way to experience Ugandan culture through its cuisine. From the ubiquitous rolex to seasonal delicacies like nsenene, these foods tell the story of Uganda's culinary traditions and innovations. Don't miss the opportunity to explore the city through its flavors - it's an essential part of any visit to Uganda's capital.
                </p>
                <p>
                  For a guided experience of Kampala's food scene, consider booking our <a href="/tours/kampala-food" className="text-ta-green hover:underline">City Walking Food Tour</a>, where our expert guides will take you to the best spots and share insights about the cultural significance of each dish.
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Kampala Food", "Street Food", "Uganda Cuisine", "Food Guide", "Local Eats"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-ta-green hover:text-white transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <FacebookShareButton
                    url={window.location.href}
                    quote="Kampala's Best Street Food Guide"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={window.location.href}
                    title="Kampala's Best Street Food Guide"
                    className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={window.location.href}
                    title="Kampala's Best Street Food Guide"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </WhatsappShareButton>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/lCTURxX.jpg"
                      alt="Sarah Namukasa"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Sarah Namukasa</h3>
                      <p className="text-gray-600">Food & Culture Guide</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Sarah is a Kampala native and food enthusiast who has been leading culinary tours for over 5 years. She specializes in connecting travelers with authentic local food experiences.
                  </p>
                  <button className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors">
                    Follow
                  </button>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#must-try" className="block text-gray-600 hover:text-ta-green">Must-Try Street Foods</a>
                    <a href="#safety-tips" className="block text-gray-600 hover:text-ta-green">Street Food Safety Tips</a>
                    <a href="#best-areas" className="block text-gray-600 hover:text-ta-green">Best Street Food Areas</a>
                    <a href="#restaurants" className="block text-gray-600 hover:text-ta-green">Local Restaurants</a>
                    <a href="#food-tours" className="block text-gray-600 hover:text-ta-green">Food Tours</a>
                    <a href="#etiquette" className="block text-gray-600 hover:text-ta-green">Etiquette and Cultural Tips</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/must-visit-places-kampala" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/EzHnSsj.jpg"
                          alt="Must-Visit Places in Kampala"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            10 Must-Visit Places in Kampala
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">7 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/hidden-gems-kampala" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/EzHnSsj.jpg"
                          alt="Hidden Gems in Kampala"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            10 Hidden Gems in Kampala You Can't Miss
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">6 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Tour Promotion */}
                <div className="bg-ta-green/10 rounded-lg border border-ta-green/20 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Experience Kampala's Food Scene</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Join our guided food tour to taste the best of Kampala's street food with expert local guides.
                  </p>
                  <Link 
                    to="/tours/kampala-food"
                    className="block w-full py-2 bg-ta-green text-white text-center rounded-lg hover:bg-ta-green-dark transition-colors"
                  >
                    Book Food Tour
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default KampalaFoodGuide;